import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Chip, Alert, Tooltip, Grid, Divider, Stack, FormControlLabel, Checkbox, Popover
} from '@mui/material';
import { Add, Delete, Edit, Upload, Download, CheckCircle, Warning, HelpOutline, Info, DocumentScanner } from '@mui/icons-material';
import { getEmployees, getYTDImports, importYTD, deleteYTDImport, importYTDCSV, markPreCutoverPaid, scanPayStub } from '../api';
import { getToken } from '../utils/tenantStorage';
import { formatCurrency } from '../utils/formatUtils';
import { useAuth } from '../context/AuthContext';
import { useConfirmDialog } from './ConfirmDialog';
import { parseCsvObjects } from '../utils/parseCsv';

const IMPORT_SOURCES = ['Gusto', 'ADP', 'Paychex', 'QuickBooks', 'Manual Entry', 'Other'];

const DEDUCTION_CATEGORIES = [
  { value: 'retirement_401k', label: '401(k)' },
  { value: 'retirement_simple_ira', label: 'SIMPLE IRA' },
  { value: 'health_insurance', label: 'Health Insurance' },
  { value: 'dental_insurance', label: 'Dental Insurance' },
  { value: 'vision_insurance', label: 'Vision Insurance' },
  { value: 'hsa', label: 'HSA' },
  { value: 'fsa', label: 'FSA' },
  { value: 'other_pretax', label: 'Other Pre-Tax' },
];

// Strip commas, dollar signs, and spaces from pasted numbers
const cleanNum = (val) => val.replace(/[,$\s]/g, '');

const emptyForm = {
  employee_id: '',
  gross_pay: '',
  federal_income_tax: '',
  social_security_tax: '',
  social_security_wages: '',
  medicare_tax: '',
  medicare_wages: '',
  state_income_tax: '',
  local_tax: '',
  bonus_pay: '',
  net_pay: '',
  pto_hours_used: '',
  pto_hours_accrued: '',
  pre_tax_deductions: [],
  post_tax_deductions: [],
};

export default function YTDImport() {
  useAuth(); // verify authenticated
  const { showConfirm, ConfirmDialogComponent } = useConfirmDialog();
  const [employees, setEmployees] = useState([]);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false); // eslint-disable-line no-unused-vars
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Import settings
  const [importSource, setImportSource] = useState('Gusto');
  const [cutoverDate, setCutoverDate] = useState(new Date().toISOString().split('T')[0]);

  // Form dialog
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [editingEmployeeName, setEditingEmployeeName] = useState('');
  const [markEntriesPaid, setMarkEntriesPaid] = useState(false);

  // CSV
  const [csvPreview, setCsvPreview] = useState(null);
  // Pay stub scan
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanEmployeeId, setScanEmployeeId] = useState('');
  // Guide
  const [guideAnchor, setGuideAnchor] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, importRes] = await Promise.all([getEmployees(), getYTDImports()]);
      setEmployees(empRes.data || []);
      setImports(importRes.data || []);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const getImportForEmployee = (empId) => imports.find(i => i.employee_id === empId);

  const handleOpenForm = (emp, existing) => {
    setEditingEmployeeName(`${emp.first_name} ${emp.last_name}`);
    if (existing) {
      const taxes = typeof existing.taxes === 'string' ? JSON.parse(existing.taxes) : (existing.taxes || {});
      const preTax = typeof existing.pre_tax_deductions === 'string' ? JSON.parse(existing.pre_tax_deductions) : (existing.pre_tax_deductions || []);
      setFormData({
        employee_id: emp.id,
        gross_pay: existing.gross_pay || '',
        federal_income_tax: taxes.federal_income || '',
        social_security_tax: taxes.social_security || '',
        social_security_wages: existing.gross_pay || '',
        medicare_tax: taxes.medicare || '',
        medicare_wages: existing.gross_pay || '',
        state_income_tax: taxes.state_income || '',
        local_tax: taxes.local_tax || '',
        bonus_pay: existing.bonus_pay || '',
        net_pay: existing.net_pay || '',
        pto_hours_used: existing.pto_hours_used || '',
        pto_hours_accrued: existing.pto_hours_accrued || '',
        pre_tax_deductions: preTax,
        post_tax_deductions: [],
      });
    } else {
      setFormData({ ...emptyForm, employee_id: emp.id });
    }
    setFormOpen(true);
  };

  const handleSubmit = async () => {
    try {
      setError('');
      await importYTD({
        ...formData,
        import_source: importSource,
        cutover_date: cutoverDate,
        mark_entries_paid: markEntriesPaid,
      });
      setSuccess(`YTD imported for ${editingEmployeeName}`);
      setFormOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import YTD');
    }
  };

  const handleDelete = async (empId, empName) => {
    const confirmed = await showConfirm({
      title: 'Remove YTD Import',
      message: `Remove imported YTD for ${empName}?`,
      confirmText: 'Remove',
      confirmColor: 'error',
      severity: 'warning'
    });
    if (!confirmed) return;
    try {
      await deleteYTDImport(empId);
      setSuccess(`YTD import removed for ${empName}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const handleAddDeduction = () => {
    setFormData(prev => ({
      ...prev,
      pre_tax_deductions: [...prev.pre_tax_deductions, { name: '', category: '', amount: '' }]
    }));
  };

  const handleDeductionChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.pre_tax_deductions];
      updated[index] = { ...updated[index], [field]: value };
      if (field === 'category') {
        const cat = DEDUCTION_CATEGORIES.find(c => c.value === value);
        if (cat) updated[index].name = cat.label;
      }
      return { ...prev, pre_tax_deductions: updated };
    });
  };

  const handleRemoveDeduction = (index) => {
    setFormData(prev => ({
      ...prev,
      pre_tax_deductions: prev.pre_tax_deductions.filter((_, i) => i !== index)
    }));
  };

  // CSV handling
  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      // Real CSV parsing: a quoted name like "Smith, Jr" must not shift the pay columns
      const { rows } = parseCsvObjects(e.target.result);
      if (rows.length < 1) { setError('CSV must have header + at least one row'); return; }
      setCsvPreview(rows);
    };
    reader.readAsText(file);
  };

  const handleCSVImport = async () => {
    if (!csvPreview) return;
    try {
      setError('');
      const result = await importYTDCSV({ rows: csvPreview, import_source: importSource, cutover_date: cutoverDate });
      setSuccess(`Imported ${result.data.imported} employees. ${result.data.failed ? `${result.data.failed} failed.` : ''}`);
      if (result.data.errors?.length > 0) {
        setError(result.data.errors.join('; '));
      }
      setCsvPreview(null);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'CSV import failed');
    }
  };

  // Pay stub scan — upload PDF, extract values, pre-fill form
  const handleScanPayStub = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Reset file input so same file can be re-selected
    event.target.value = '';

    setScanning(true);
    setError('');
    setScanResult(null);

    try {
      const res = await scanPayStub(file);
      const data = res.data;

      if (!data.success) {
        setError(data.error || 'Could not extract text from PDF');
        setScanning(false);
        return;
      }

      setScanResult(data);
      setScanning(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to scan pay stub');
      setScanning(false);
    }
  };

  const handleApplyScanResult = (emp) => {
    if (!scanResult) return;
    const v = scanResult.values;
    setEditingEmployeeName(`${emp.first_name} ${emp.last_name}`);
    setFormData({
      employee_id: emp.id,
      gross_pay: v.gross_pay || '',
      federal_income_tax: v.federal_income_tax || '',
      social_security_tax: v.social_security_tax || '',
      social_security_wages: v.social_security_wages || v.gross_pay || '',
      medicare_tax: v.medicare_tax || '',
      medicare_wages: v.medicare_wages || v.gross_pay || '',
      state_income_tax: v.state_income_tax || '',
      local_tax: v.local_tax || '',
      bonus_pay: v.bonus_pay || '',
      net_pay: v.net_pay || '',
      pto_hours_used: '',
      pto_hours_accrued: '',
      pre_tax_deductions: scanResult.pre_tax_deductions || [],
      post_tax_deductions: [],
    });
    setScanResult(null);
    setScanEmployeeId('');
    setFormOpen(true);
  };


  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">YTD Import — Payroll Cutover</Typography>
        <Tooltip title="How it works">
          <IconButton onClick={e => setGuideAnchor(e.currentTarget)} size="small" sx={{ ml: 1 }}>
            <HelpOutline color="action" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Guide Popover */}
      <Popover open={Boolean(guideAnchor)} anchorEl={guideAnchor} onClose={() => setGuideAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Paper sx={{ p: 2.5, maxWidth: 450 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>YTD Import Guide</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            When switching payroll providers mid-year, you must import year-to-date totals so HourTrax
            can calculate taxes correctly for the rest of the year.
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>Steps:</Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5, '& li': { mb: 1 } }}>
            <Typography component="li" variant="body2">Set your <strong>cutover date</strong> — the last day covered by your previous provider</Typography>
            <Typography component="li" variant="body2">For each employee, enter their <strong>YTD totals</strong> from the previous provider's last pay stub or payroll summary</Typography>
            <Typography component="li" variant="body2">Optionally check <strong>"Mark entries as paid"</strong> if you have time entries in HourTrax from before the cutover that were already paid through the old provider</Typography>
            <Typography component="li" variant="body2">Create your first HourTrax <strong>pay period starting the day after</strong> the cutover date</Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>Where to find YTD data:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="body2"><strong>Gusto:</strong> Reports → Payroll Journal → filter to current year</Typography>
            <Typography component="li" variant="body2"><strong>ADP:</strong> Reports → Tax Reports → Employee YTD Summary</Typography>
            <Typography component="li" variant="body2"><strong>Paychex:</strong> Reports → Payroll → YTD Earnings Summary</Typography>
            <Typography component="li" variant="body2"><strong>Any provider:</strong> Use the last pay stub of the year — it has YTD totals</Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" gutterBottom>Why this matters:</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
            <Typography component="li" variant="body2"><strong>Social Security:</strong> Withholding stops at $184,500 — without YTD, employees would be double-taxed</Typography>
            <Typography component="li" variant="body2"><strong>Medicare:</strong> Additional 0.9% tax kicks in above $200,000 cumulative</Typography>
            <Typography component="li" variant="body2"><strong>Federal withholding:</strong> Progressive brackets need YTD context for accurate annualization</Typography>
            <Typography component="li" variant="body2"><strong>W-2:</strong> End-of-year W-2 will include both providers' data for a complete annual record</Typography>
          </Box>
        </Paper>
      </Popover>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Import Settings */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Import Source" value={importSource} onChange={e => setImportSource(e.target.value)}>
              {IMPORT_SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Cutover Date" type="date" value={cutoverDate}
              onChange={e => setCutoverDate(e.target.value)} InputLabelProps={{ shrink: true }}
              helperText="Last day covered by previous provider"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" startIcon={<Download />} size="small"
                onClick={async () => {
                  try {
                    const res = await fetch('/api/payroll/import-ytd/template', {
                      headers: { 'Authorization': `Bearer ${getToken()}` }
                    });
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'ytd_import_template.csv';
                    a.click();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    setError('Failed to download template');
                  }
                }}>
                CSV Template
              </Button>
              <Button variant="outlined" startIcon={<Upload />} size="small" component="label">
                Upload CSV
                <input type="file" accept=".csv" hidden onChange={handleCSVUpload} />
              </Button>
              <Tooltip title="Upload a pay stub PDF to automatically extract YTD values. Works with digital PDFs from ADP, Gusto, Paychex, QuickBooks, and most providers.">
                <span>
                  <Button variant="contained" startIcon={<DocumentScanner />} size="small" component="label" disabled={scanning}>
                    {scanning ? 'Scanning...' : 'Scan Pay Stub'}
                    <input type="file" accept=".pdf" hidden onChange={handleScanPayStub} />
                  </Button>
                </span>
              </Tooltip>
              <Button variant="outlined" color="warning" startIcon={<CheckCircle />} size="small"
                onClick={async () => {
                  if (!cutoverDate) { setError('Set cutover date first'); return; }
                  const confirmed = await showConfirm({
                    title: 'Mark Pre-Cutover Entries Paid',
                    message: `Mark ALL time entries on or before ${cutoverDate} as paid? This prevents them from being picked up by HourTrax payroll.`,
                    confirmText: 'Mark Paid',
                    confirmColor: 'warning',
                    severity: 'warning'
                  });
                  if (!confirmed) return;
                  try {
                    const res = await markPreCutoverPaid(cutoverDate);
                    setSuccess(res.data.message);
                  } catch (err) {
                    setError(err.response?.data?.error || 'Failed to mark entries');
                  }
                }}>
                Mark Pre-Cutover Entries Paid
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* CSV Preview */}
      {csvPreview && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#fff8e1' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>CSV Preview — {csvPreview.length} employees</Typography>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Fed Tax</TableCell>
                  <TableCell align="right">SS Tax</TableCell>
                  <TableCell align="right">Medicare</TableCell>
                  <TableCell align="right">State Tax</TableCell>
                  <TableCell align="right">Net</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {csvPreview.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.first_name} {row.last_name}</TableCell>
                    <TableCell align="right">{formatCurrency(row.gross_pay)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.federal_income_tax)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.social_security_tax)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.medicare_tax)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.state_income_tax)}</TableCell>
                    <TableCell align="right">{formatCurrency(row.net_pay)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleCSVImport}>Import {csvPreview.length} Employees</Button>
            <Button variant="outlined" onClick={() => setCsvPreview(null)}>Cancel</Button>
          </Box>
        </Paper>
      )}

      {/* Scan Result Preview */}
      {scanResult && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: scanResult.confidence === 'high' ? '#e8f5e9' : scanResult.confidence === 'medium' ? '#fff8e1' : '#fce4ec' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                <DocumentScanner sx={{ verticalAlign: 'middle', mr: 1 }} />
                Pay Stub Scan Results
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Detected provider: <strong>{scanResult.provider}</strong> | Confidence: <strong>{scanResult.confidence}</strong> ({scanResult.fields_extracted}/{scanResult.fields_total} fields)
                {scanResult.employee_name && <> | Employee: <strong>{scanResult.employee_name}</strong></>}
              </Typography>
            </Box>
            <Button variant="text" size="small" onClick={() => setScanResult(null)}>Dismiss</Button>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Gross Pay', value: scanResult.values.gross_pay },
              { label: 'Federal Tax', value: scanResult.values.federal_income_tax },
              { label: 'SS Tax', value: scanResult.values.social_security_tax },
              { label: 'Medicare', value: scanResult.values.medicare_tax },
              { label: 'State Tax', value: scanResult.values.state_income_tax },
              { label: 'Net Pay', value: scanResult.values.net_pay },
            ].map(({ label, value }) => (
              <Grid item xs={6} sm={2} key={label}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="body1" fontWeight="bold">{value ? formatCurrency(value) : '—'}</Typography>
              </Grid>
            ))}
          </Grid>

          {scanResult.pre_tax_deductions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">Pre-Tax Deductions Detected:</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                {scanResult.pre_tax_deductions.map((d, i) => (
                  <Chip key={i} size="small" label={`${d.name}: ${formatCurrency(d.amount)}`} />
                ))}
              </Box>
            </Box>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            Review extracted values carefully — select an employee below to pre-fill their YTD import form with these values.
          </Alert>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              select fullWidth size="small" label="Apply to Employee"
              value={scanEmployeeId} onChange={e => setScanEmployeeId(e.target.value)}
              sx={{ maxWidth: 300 }}
            >
              {employees.map(emp => (
                <MenuItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained" color="primary"
              disabled={!scanEmployeeId}
              onClick={() => {
                const emp = employees.find(e => e.id === parseInt(scanEmployeeId));
                if (emp) handleApplyScanResult(emp);
              }}
            >
              Pre-fill Form
            </Button>
          </Box>
        </Paper>
      )}

      {/* Employee Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell align="right">YTD Gross</TableCell>
              <TableCell align="right">Fed Withheld</TableCell>
              <TableCell align="right">SS Tax</TableCell>
              <TableCell align="right">Medicare</TableCell>
              <TableCell align="right">State Tax</TableCell>
              <TableCell align="right">Net Pay</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map(emp => {
              const imported = getImportForEmployee(emp.id);
              const taxes = imported ? (typeof imported.taxes === 'string' ? JSON.parse(imported.taxes) : (imported.taxes || {})) : {};
              return (
                <TableRow key={emp.id} hover>
                  <TableCell>{emp.first_name} {emp.last_name}</TableCell>
                  <TableCell align="right">{imported ? formatCurrency(imported.gross_pay) : '—'}</TableCell>
                  <TableCell align="right">{imported ? formatCurrency(taxes.federal_income) : '—'}</TableCell>
                  <TableCell align="right">{imported ? formatCurrency(taxes.social_security) : '—'}</TableCell>
                  <TableCell align="right">{imported ? formatCurrency(taxes.medicare) : '—'}</TableCell>
                  <TableCell align="right">{imported ? formatCurrency(taxes.state_income) : '—'}</TableCell>
                  <TableCell align="right">{imported ? formatCurrency(imported.net_pay) : '—'}</TableCell>
                  <TableCell>
                    {imported ? (
                      <Chip size="small" icon={<CheckCircle />} label="Imported" color="success" />
                    ) : (
                      <Chip size="small" icon={<Warning />} label="Not Imported" color="default" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={imported ? 'Edit import' : 'Import YTD'}>
                      <IconButton size="small" color="primary" onClick={() => handleOpenForm(emp, imported)}>
                        {imported ? <Edit fontSize="small" /> : <Add fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    {imported && (
                      <Tooltip title="Remove import">
                        <IconButton size="small" color="error" onClick={() => handleDelete(emp.id, `${emp.first_name} ${emp.last_name}`)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {employees.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">No employees found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Manual Entry Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>YTD Import — {editingEmployeeName}</DialogTitle>
        <DialogContent>
          <Alert severity="info" icon={<Info />} sx={{ mb: 2 }}>
            Enter <strong>cumulative year-to-date totals</strong> from {importSource} through {cutoverDate}.
            These values can be found on the employee's last pay stub or your provider's YTD payroll summary report.
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12}><Divider><Typography variant="caption">Earnings</Typography></Divider></Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Gross Pay" InputLabelProps={{ shrink: true }} value={formData.gross_pay}
                onChange={e => {
                  const val = cleanNum(e.target.value);
                  setFormData(p => ({
                    ...p, gross_pay: val,
                    social_security_wages: (!p.social_security_wages || p.social_security_wages === '') ? val : p.social_security_wages,
                    medicare_wages: (!p.medicare_wages || p.medicare_wages === '') ? val : p.medicare_wages,
                  }));
                }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Bonus/Supplemental Pay" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.bonus_pay}
                onChange={e => setFormData(p => ({ ...p, bonus_pay: cleanNum(e.target.value) }))} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField fullWidth size="small" label="Net Pay" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.net_pay}
                onChange={e => setFormData(p => ({ ...p, net_pay: cleanNum(e.target.value) }))} />
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">Federal Taxes Withheld</Typography></Divider></Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Federal Income Tax" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.federal_income_tax}
                onChange={e => setFormData(p => ({ ...p, federal_income_tax: cleanNum(e.target.value) }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Social Security Tax" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.social_security_tax}
                onChange={e => setFormData(p => ({ ...p, social_security_tax: cleanNum(e.target.value) }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Medicare Tax" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.medicare_tax}
                onChange={e => setFormData(p => ({ ...p, medicare_tax: cleanNum(e.target.value) }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="State Income Tax" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.state_income_tax}
                onChange={e => setFormData(p => ({ ...p, state_income_tax: cleanNum(e.target.value) }))} />
            </Grid>

            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="SS Wages" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.social_security_wages}
                onChange={e => setFormData(p => ({ ...p, social_security_wages: cleanNum(e.target.value) }))}
                helperText="Auto-filled from gross. Override if capped at $184,500" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Medicare Wages" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.medicare_wages}
                onChange={e => setFormData(p => ({ ...p, medicare_wages: cleanNum(e.target.value) }))}
                helperText="Auto-filled from gross. No cap applies." />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="Local Tax" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.local_tax}
                onChange={e => setFormData(p => ({ ...p, local_tax: cleanNum(e.target.value) }))} />
            </Grid>

            <Grid item xs={12}>
              <Divider><Typography variant="caption">Pre-Tax Deductions</Typography></Divider>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Enter YTD totals for deductions taken before taxes. Common pre-tax deductions include
                401(k)/retirement contributions, health/dental/vision insurance premiums, HSA and FSA contributions.
                These reduce taxable income and affect W-2 Box 12 reporting.
              </Typography>
            </Grid>
            {formData.pre_tax_deductions.map((ded, i) => (
              <Grid container spacing={1} key={i} sx={{ mb: 1, ml: 0 }}>
                <Grid item xs={6}>
                  <TextField select fullWidth size="small" label="Category" value={ded.category}
                    onChange={e => handleDeductionChange(i, 'category', e.target.value)}>
                    {DEDUCTION_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={4}>
                  <TextField fullWidth size="small" label="YTD Amount" InputLabelProps={{ shrink: true }} inputMode="decimal" value={ded.amount}
                    onChange={e => handleDeductionChange(i, 'amount', cleanNum(e.target.value))} />
                </Grid>
                <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton size="small" color="error" onClick={() => handleRemoveDeduction(i)}><Delete /></IconButton>
                </Grid>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button size="small" startIcon={<Add />} onClick={handleAddDeduction}>Add Deduction</Button>
            </Grid>

            <Grid item xs={12}><Divider><Typography variant="caption">PTO (Optional)</Typography></Divider></Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="PTO Hours Used" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.pto_hours_used}
                onChange={e => setFormData(p => ({ ...p, pto_hours_used: cleanNum(e.target.value) }))} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth size="small" label="PTO Hours Accrued" InputLabelProps={{ shrink: true }} inputMode="decimal" value={formData.pto_hours_accrued}
                onChange={e => setFormData(p => ({ ...p, pto_hours_accrued: cleanNum(e.target.value) }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          <FormControlLabel
            control={<Checkbox checked={markEntriesPaid} onChange={e => setMarkEntriesPaid(e.target.checked)} size="small" />}
            label={<Typography variant="body2">Mark this employee's time entries through {cutoverDate} as paid</Typography>}
          />
          <Box>
            <Button onClick={() => setFormOpen(false)} sx={{ mr: 1 }}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={!formData.gross_pay}>
              Import YTD
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <ConfirmDialogComponent />
    </Box>
  );
}
